import { updateFeedbackById } from "@/api/feedback/feedback";
import {
    Select,
    MenuItem,
    ListItemIcon,
    ListItemText,
    SelectChangeEvent,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import CheckIcon from "@mui/icons-material/Check";
import { useQueryClient, useMutation } from "@tanstack/react-query";

const STATUS_OPTIONS = ["Unresolved", "Under Review", "Resolved"];

import { FeedbackStatus, IFeedback } from "@/api";
import { useGridApiContext } from "@mui/x-data-grid";
import { enqueueSnackbar } from "notistack";

interface EditFeedbackStatusProps {
    id: number;
    feedback: IFeedback;
}

export function EditFeedbackStatus({
    id,
    feedback: initialFeedback,
}: EditFeedbackStatusProps) {
    const apiRef = useGridApiContext();
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: ({
            id,
            newStatus,
        }: {
            id: number;
            newStatus: FeedbackStatus;
        }) => {
            apiRef.current.stopCellEditMode({
                id,
                field: "status",
                cellToFocusAfter: "below",
            });
            return updateFeedbackById(id, { status: newStatus });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-feedback"] });
        },
        onError: () => {
            enqueueSnackbar("Failed to update feedback status", {
                variant: "error",
            });
        },
    });

    const handleChange = async (event: SelectChangeEvent<FeedbackStatus>) => {
        mutation.mutate({
            id: id,
            newStatus: event.target.value as FeedbackStatus,
        });
    };

    const onClose = async () => {
        apiRef.current.stopCellEditMode({
            id,
            field: "status",
            ignoreModifications: true,
        });
    };

    return (
        <Select
            value={initialFeedback.status}
            onChange={handleChange}
            MenuProps={{
                onClose: onClose,
            }}
            sx={{
                height: "100%",
                "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    pl: 1,
                },
            }}
            autoFocus
            fullWidth
            open
        >
            {STATUS_OPTIONS.map((option) => {
                let IconComponent = undefined;
                if (option === "Resolved") {
                    IconComponent = CheckIcon;
                } else if (option === "Under Review") {
                    IconComponent = InfoIcon;
                } else {
                    IconComponent = WarningIcon;
                }

                const label = option;

                return (
                    <MenuItem key={option} value={option}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <IconComponent fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                            primary={label}
                            sx={{ overflow: "hidden" }}
                        />
                    </MenuItem>
                );
            })}
        </Select>
    );
}
